import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, CheckSquare, Sparkles, 
  Barcode, Search, Filter, 
  Plus, Clock, AlertCircle,
  ClipboardCheck, ArrowRight,
  PackageCheck, Tag, X, FileUp, UploadCloud,
  FileText, CheckCircle2, Loader2, Info,
  FileCheck, CreditCard
} from 'lucide-react';
import { incomingService } from '../../../../services/erpApi';
import { Toast } from '../../../../components';
import { useToast } from '../../../../hooks/useToast';
import './IncomingDashboard.css';

interface InvoiceEntry {
  id: number;
  number: string;
  supplier_id: number;
  supplier_name?: string;
  status: 'pending' | 'processed' | 'inspection' | 'awaiting_launch' | 'awaiting_addressing' | 'launched' | 'accounts_payable_pending' | 'priced' | 'completed' | 'cancelled' | 'error';
  entry_date: string;
  total_amount: number;
  items_count?: number;
  created_at: string;
  accounts_payable_status?: 'not_created' | 'has_pending' | 'all_paid' | 'has_overdue' | 'mixed';
  launched_status?: string;
  launched_at?: string;
  launched_by?: number;
  skip_inspection?: boolean;
}

interface WorkflowStep {
  label: string;
  count: number;
  icon: any;
  color: string;
  bg: string;
}

const IncomingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [invoiceEntries, setInvoiceEntries] = useState<InvoiceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedFileForCreate, setSelectedFileForCreate] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);

  // Workflow: Recebidos - Triagem - Conferência - Aguard. Lançamento - Aguard. Endereçamento - Contas - Precificação - Concluídos
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { label: 'Recebidos', count: 0, icon: PackageCheck, color: 'step-emerald', bg: '' },
    { label: 'Triagem', count: 0, icon: FileCheck, color: 'step-slate', bg: '' },
    { label: 'Conferência', count: 0, icon: Barcode, color: 'step-amber', bg: '' },
    { label: 'Aguard. Lançamento', count: 0, icon: ClipboardCheck, color: 'step-blue', bg: '' },
    { label: 'Aguard. Endereçamento', count: 0, icon: Clock, color: 'step-blue', bg: '' },
    { label: 'Contas', count: 0, icon: CreditCard, color: 'step-purple', bg: '' },
    { label: 'Precificação', count: 0, icon: Tag, color: 'step-rose', bg: '' },
    { label: 'Concluídos', count: 0, icon: CheckCircle2, color: 'step-emerald', bg: '' },
  ]);

  const loadInvoiceEntries = useCallback(async () => {
    try {
      setLoading(true);
      // Buscar todos os status relevantes do fluxo para mostrar no dashboard
      const [pendingResponse, processedResponse, inspectionResponse, awaitingLaunchResponse, awaitingAddressingResponse, launchedResponse, accountsPayablePendingResponse, pricedResponse, completedResponse] = await Promise.all([
        incomingService.list({ status: 'pending', limit: 1000 }),
        incomingService.list({ status: 'processed', limit: 1000 }),
        incomingService.list({ status: 'inspection', limit: 1000 }),
        incomingService.list({ status: 'awaiting_launch', limit: 1000 }),
        incomingService.list({ status: 'awaiting_addressing', limit: 1000 }),
        incomingService.list({ status: 'launched', limit: 1000 }),
        incomingService.list({ status: 'accounts_payable_pending', limit: 1000 }),
        incomingService.list({ status: 'priced', limit: 1000 }),
        incomingService.list({ status: 'completed', limit: 1000 })
      ]);
      
      const allEntries: InvoiceEntry[] = [
        ...(pendingResponse.success ? pendingResponse.data || [] : []),
        ...(processedResponse.success ? processedResponse.data || [] : []),
        ...(inspectionResponse.success ? inspectionResponse.data || [] : []),
        ...(awaitingLaunchResponse.success ? awaitingLaunchResponse.data || [] : []),
        ...(awaitingAddressingResponse.success ? awaitingAddressingResponse.data || [] : []),
        ...(launchedResponse.success ? launchedResponse.data || [] : []),
        ...(accountsPayablePendingResponse.success ? accountsPayablePendingResponse.data || [] : []),
        ...(pricedResponse.success ? pricedResponse.data || [] : []),
        ...(completedResponse.success ? completedResponse.data || [] : [])
      ];
      
      setInvoiceEntries(allEntries);
      // Atualizar contadores do workflow
      updateWorkflowCounts(allEntries);
    } catch (error: any) {
      console.error('Erro ao carregar invoice entries:', error);
      showToast('Erro ao carregar notas fiscais', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadInvoiceEntries();
  }, [loadInvoiceEntries]);

  const updateWorkflowCounts = (entries: InvoiceEntry[]) => {
    // Buscar também notas com status 'accounts_payable_pending' para o contador de Contas
    const accountsPayablePending = entries.filter(e => 
      e.status === 'accounts_payable_pending' || 
      (e.status === 'launched' && (e.accounts_payable_status === 'not_created' || !e.accounts_payable_status))
    );
    
    // Filtrar notas em awaiting_launch que já foram lançadas (status incorreto no banco)
    const awaitingLaunchCount = entries.filter(e => {
      if (e.status === 'awaiting_launch') {
        const isLaunched = e.launched_at || e.launched_status === 'launched';
        return !isLaunched; // Só contar se realmente não foi lançado
      }
      return false;
    }).length;
    
    // Contar awaiting_addressing incluindo notas com status awaiting_launch mas já lançadas
    const awaitingAddressingCount = entries.filter(e => {
      if (e.status === 'awaiting_addressing') return true;
      if (e.status === 'awaiting_launch') {
        const isLaunched = e.launched_at || e.launched_status === 'launched';
        return isLaunched; // Contar como awaiting_addressing se já foi lançado
      }
      return false;
    }).length;
    
    setWorkflowSteps([
      { label: 'Recebidos', count: entries.filter(e => e.status === 'pending').length, icon: PackageCheck, color: 'step-emerald', bg: '' },
      { label: 'Triagem', count: entries.filter(e => e.status === 'processed').length, icon: FileCheck, color: 'step-slate', bg: '' },
      { label: 'Conferência', count: entries.filter(e => e.status === 'inspection').length, icon: Barcode, color: 'step-amber', bg: '' },
      { label: 'Aguard. Lançamento', count: awaitingLaunchCount, icon: ClipboardCheck, color: 'step-blue', bg: '' },
      { label: 'Aguard. Endereçamento', count: awaitingAddressingCount, icon: Clock, color: 'step-blue', bg: '' },
      { label: 'Contas', count: accountsPayablePending.length, icon: CreditCard, color: 'step-purple', bg: '' },
      { label: 'Precificação', count: entries.filter(e => e.status === 'priced').length, icon: Tag, color: 'step-rose', bg: '' },
      { label: 'Concluídos', count: entries.filter(e => e.status === 'completed').length, icon: CheckCircle2, color: 'step-emerald', bg: '' },
    ]);
  };

  const getStatusConfig = (invoice: InvoiceEntry) => {
    const status = invoice.status;
    const isLaunched = invoice.launched_at || invoice.launched_status === 'launched';
    
    // Se o estoque já foi lançado mas o status está incorreto, corrigir a exibição
    if (isLaunched && status === 'awaiting_launch') {
      // Se já foi lançado mas status está como awaiting_launch, tratar como awaiting_addressing
      return { label: 'Aguard. Endereçamento', color: 'status-awaiting-addressing', icon: Clock };
    }
    
    switch (status) {
      case 'pending': return { label: 'Recebido / NF ok', color: 'status-received', icon: PackageCheck };
      case 'processed': return { label: 'Processado', color: 'status-processed', icon: CheckCircle2 };
      case 'inspection': return { label: 'Em Conferência', color: 'status-inspection', icon: Barcode };
      case 'awaiting_launch': return { label: 'Aguardando Lançamento', color: 'status-awaiting-launch', icon: Clock };
      case 'awaiting_addressing': return { label: 'Aguard. Endereçamento', color: 'status-awaiting-addressing', icon: Clock };
      case 'launched': return { label: 'Lançado', color: 'status-launched', icon: CheckSquare };
      case 'accounts_payable_pending': return { label: 'Aguard. Lançamento Contas', color: 'status-accounts-payable', icon: CreditCard };
      case 'priced': return { label: 'Precificado', color: 'status-priced', icon: Tag };
      case 'completed': return { label: 'Concluído', color: 'status-completed', icon: CheckCircle2 };
      case 'cancelled': return { label: 'Cancelado', color: 'status-cancelled', icon: X };
      case 'error': return { label: 'Erro', color: 'status-error', icon: AlertCircle };
      default: return { label: 'Em Processo', color: 'status-default', icon: Box };
    }
  };

  const handleRowClick = async (invoice: InvoiceEntry) => {
    const isLaunched = invoice.launched_at || invoice.launched_status === 'launched';
    
    // Se o estoque já foi lançado mas o status está incorreto, tratar como awaiting_addressing
    if (isLaunched && invoice.status === 'awaiting_launch') {
      navigate(`/erp/stock/incoming/addressing/${invoice.id}`);
      return;
    }
    
    if (invoice.status === 'pending') {
      // Se ainda não tem XML, abrir modal de upload
      setSelectedInvoiceId(invoice.id);
      setIsImportModalOpen(true);
    } else if (invoice.status === 'processed') {
      // Se já processou XML, sempre ir para página de validação/comparação
      // Mesmo que skip_inspection seja true, o usuário pode precisar corrigir erros de conversão
      navigate(`/erp/stock/incoming/validation/${invoice.id}`);
    } else if (invoice.status === 'inspection') {
      // Se está em inspeção, ir para página de inspeção
      navigate(`/erp/stock/incoming/inspection/${invoice.id}`);
    } else if (invoice.status === 'awaiting_launch') {
      // Se está aguardando lançamento, ir para página de lançamento
      navigate(`/erp/stock/incoming/launch/${invoice.id}`);
    } else if (invoice.status === 'awaiting_addressing') {
      // Se está aguardando endereçamento, ir para página de endereçamento
      navigate(`/erp/stock/incoming/addressing/${invoice.id}`);
    } else if (invoice.status === 'launched') {
      // Se já foi lançado, verificar se todos os itens foram endereçados
      try {
        const itemsResponse = await incomingService.getItems(invoice.id);
        if (itemsResponse.success && itemsResponse.data) {
          const items = itemsResponse.data;
          const allAddressed = items.length > 0 && items.every((item: any) => item.location_id !== null && item.location_id !== undefined);
          
          if (allAddressed) {
            // Se todos os itens foram endereçados, verificar se já criou contas
            const apStatus = invoice.accounts_payable_status;
            if (apStatus === 'not_created' || !apStatus) {
              // Se ainda não criou contas, ir para página de criação de contas a pagar
              navigate(`/erp/stock/incoming/accounts-payable/${invoice.id}`);
            } else {
              // Se já criou contas, ir para precificação
              navigate(`/erp/stock/incoming/pricing/${invoice.id}`);
            }
          } else {
            // Se ainda há itens sem endereço, ir para página de endereçamento
            navigate(`/erp/stock/incoming/addressing/${invoice.id}`);
          }
        } else {
          // Se não conseguir buscar itens, ir para endereçamento por padrão
          navigate(`/erp/stock/incoming/addressing/${invoice.id}`);
        }
      } catch (error) {
        console.error('Erro ao verificar endereçamento:', error);
        // Em caso de erro, ir para endereçamento por padrão
        navigate(`/erp/stock/incoming/addressing/${invoice.id}`);
      }
    } else if (invoice.status === 'accounts_payable_pending') {
      // Se está aguardando contas a pagar, ir para página de criação de contas a pagar
      navigate(`/erp/stock/incoming/accounts-payable/${invoice.id}`);
    } else if (invoice.status === 'priced') {
      // Se já criou contas e está precificado, ir para página de precificação (para revisar)
      navigate(`/erp/stock/incoming/pricing/${invoice.id}`);
    } else if (invoice.status === 'completed') {
      // Se já está completa, navegar para página de visualização
      navigate(`/erp/stock/incoming/view/${invoice.id}`);
    } else {
      navigate(`/erp/stock/incoming/inspection/${invoice.id}`);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'application/xml' || file.type === 'text/xml' || file.name.endsWith('.xml')) {
        setSelectedFile(file);
      } else {
        showToast('Por favor, selecione um arquivo XML', 'error');
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedInvoiceId) return;

    setIsUploading(true);
    try {
      const response = await incomingService.uploadXml(selectedInvoiceId, selectedFile);
      if (response.success) {
        showToast('XML importado com sucesso!', 'success');
        setIsImportModalOpen(false);
        setSelectedFile(null);
        setSelectedInvoiceId(null);
        loadInvoiceEntries();
      } else {
        showToast(response.message || 'Erro ao importar XML', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      showToast(error.response?.data?.message || 'Erro ao fazer upload do XML', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelectForCreate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'application/xml' || file.type === 'text/xml' || file.name.endsWith('.xml')) {
        setSelectedFileForCreate(file);
      } else {
        showToast('Por favor, selecione um arquivo XML', 'error');
      }
    }
  };

  const handleCreateFromXml = async () => {
    if (!selectedFileForCreate) return;

    setIsCreating(true);
    try {
      const response = await incomingService.createFromXml(selectedFileForCreate);
      if (response.success) {
        showToast('Nota fiscal criada com sucesso a partir do XML!', 'success');
        setIsCreateModalOpen(false);
        setSelectedFileForCreate(null);
        loadInvoiceEntries();
        // Redirecionar para página de validação
        setTimeout(() => {
          navigate(`/erp/stock/incoming/validation/${response.data.id}`);
        }, 1000);
      } else {
        showToast(response.message || 'Erro ao criar nota fiscal do XML', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao criar nota fiscal do XML:', error);
      showToast(error.response?.data?.message || 'Erro ao criar nota fiscal do XML', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  // Mapeamento entre labels do workflow e status reais
  const getStatusFromLabel = (label: string): string[] | null => {
    const statusMap: Record<string, string[]> = {
      'Recebidos': ['pending'],
      'Triagem': ['processed'],
      'Conferência': ['inspection'],
      'Aguard. Lançamento': ['awaiting_launch'],
      'Aguard. Endereçamento': ['awaiting_addressing'],
      'Contas': ['accounts_payable_pending', 'launched'],
      'Precificação': ['priced'],
      'Concluídos': ['completed']
    };
    return statusMap[label] || null;
  };

  const filteredEntries = invoiceEntries.filter(entry => {
    // Filtro por status
    if (selectedStatusFilter) {
      const statuses = getStatusFromLabel(selectedStatusFilter);
      if (statuses) {
        // Para o status "Contas", verificar também se está em launched com accounts_payable_status
        if (selectedStatusFilter === 'Contas') {
          const matchesStatus = statuses.includes(entry.status);
          const matchesLaunchedWithAP = entry.status === 'launched' && 
            (entry.accounts_payable_status === 'not_created' || !entry.accounts_payable_status);
          if (!matchesStatus && !matchesLaunchedWithAP) return false;
        } else {
          // Para "Aguard. Lançamento", verificar se realmente não foi lançado
          if (selectedStatusFilter === 'Aguard. Lançamento') {
            if (entry.status === 'awaiting_launch') {
              const isLaunched = entry.launched_at || entry.launched_status === 'launched';
              if (isLaunched) return false; // Não mostrar se já foi lançado
            } else if (!statuses.includes(entry.status)) {
              return false;
            }
          } else if (selectedStatusFilter === 'Aguard. Endereçamento') {
            // Para "Aguard. Endereçamento", incluir também awaiting_launch que já foi lançado
            if (entry.status === 'awaiting_addressing') {
              return true;
            } else if (entry.status === 'awaiting_launch') {
              const isLaunched = entry.launched_at || entry.launched_status === 'launched';
              return isLaunched; // Mostrar se já foi lançado
            } else {
              return false;
            }
          } else if (!statuses.includes(entry.status)) {
            return false;
          }
        }
      }
    }

    // Filtro por busca
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.number?.toLowerCase().includes(search) ||
      entry.supplier_name?.toLowerCase().includes(search) ||
      entry.id.toString().includes(search)
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Agora';
    if (hours < 24) return `Há ${hours}h`;
    if (days === 1) return 'Ontem';
    return `Há ${days}d`;
  };

  return (
    <div className="incoming-dashboard">
      {/* Header */}
      <div className="incoming-dashboard-header">
        <div>
          <h1 className="incoming-dashboard-title">
            Entrada de <span className="incoming-dashboard-title-accent">Mercadoria</span>
          </h1>
          <p className="incoming-dashboard-subtitle">
            Gestão de Recebimento Pós-Transporte e Intralogística
          </p>
        </div>
        <div className="incoming-dashboard-actions">
          <button className="incoming-dashboard-btn-secondary">
            <Filter size={16} /> Filtros
          </button>
          <button 
            className="incoming-dashboard-btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus size={16} /> Importar Nova NF
          </button>
        </div>
      </div>

      {/* Pipeline workflow bar - Filtro por Status */}
      <div className="incoming-dashboard-workflow">
        <div className="incoming-dashboard-workflow-header">
          <h3>
            <Filter size={16} />
            Filtro por Status
          </h3>
          {selectedStatusFilter && (
            <button
              className="incoming-dashboard-workflow-clear-filter"
              onClick={() => setSelectedStatusFilter(null)}
            >
              <X size={14} />
              Limpar Filtro
            </button>
          )}
        </div>
        <div className="incoming-dashboard-workflow-grid">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = selectedStatusFilter === step.label;
            return (
              <div 
                key={idx} 
                className={`incoming-dashboard-workflow-step ${step.color} ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (isActive) {
                    // Se já está ativo, desmarcar
                    setSelectedStatusFilter(null);
                  } else {
                    // Ativar filtro
                    setSelectedStatusFilter(step.label);
                  }
                }}
              >
                <div className="incoming-dashboard-workflow-icon">
                  <Icon size={24} />
                  {step.count > 0 && (
                    <div className="incoming-dashboard-workflow-badge">
                      {step.count}
                    </div>
                  )}
                </div>
                <p className="incoming-dashboard-workflow-label">{step.label}</p>
                {idx < workflowSteps.length - 1 && (
                  <div className="incoming-dashboard-workflow-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Active Loads List */}
      <div className="incoming-dashboard-card">
        <div className="incoming-dashboard-card-header">
          <h3 className="incoming-dashboard-card-title">Cargas Disponíveis no Armazém</h3>
          <div className="incoming-dashboard-search-wrapper">
            <div className="incoming-dashboard-search">
              <Search className="incoming-dashboard-search-icon" size={14} />
              <input
                type="text"
                placeholder="Buscar por NF ou Fornecedor..."
                className="incoming-dashboard-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="incoming-dashboard-loading">
            <Loader2 size={24} className="incoming-dashboard-spinner" />
            <p>Carregando...</p>
          </div>
        ) : (
          <div className="incoming-dashboard-table-wrapper">
            <table className="incoming-dashboard-table">
              <thead>
                <tr>
                  <th>Protocolo / NF</th>
                  <th>Origem / Fornecedor</th>
                  <th className="text-center">Volume Total</th>
                  <th className="text-center">Status Interno</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="incoming-dashboard-empty">
                      <p>Nenhuma nota fiscal encontrada</p>
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((invoice) => {
                    const config = getStatusConfig(invoice);
                    const Icon = config.icon;
                    return (
                      <tr
                        key={invoice.id}
                        className="incoming-dashboard-table-row"
                        onClick={() => handleRowClick(invoice)}
                      >
                        <td>
                          <div className="incoming-dashboard-invoice-info">
                            <span className="incoming-dashboard-invoice-id">IN-{invoice.id}</span>
                            <span className="incoming-dashboard-invoice-number">
                              <CheckCircle2 size={10} /> {invoice.number || 'Sem número'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="incoming-dashboard-supplier-name">
                            {invoice.supplier_name || `Fornecedor #${invoice.supplier_id}`}
                          </span>
                          <p className="incoming-dashboard-date">
                            <Clock size={10} /> {formatDate(invoice.created_at)}
                          </p>
                        </td>
                        <td className="text-center">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                            <span className="incoming-dashboard-items-badge">
                              {invoice.items_count || 0} Itens
                            </span>
                            {invoice.total_amount && invoice.total_amount > 0 && (
                              <span style={{ 
                                fontSize: '9px', 
                                color: 'var(--text-secondary)', 
                                fontWeight: 'var(--font-weight-semibold)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                R$ {invoice.total_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="incoming-dashboard-status-wrapper">
                            <span className={`incoming-dashboard-status ${config.color}`}>
                              <Icon size={12} />
                              {config.label}
                            </span>
                          </div>
                        </td>
                        <td className="text-right">
                          <button 
                            className="incoming-dashboard-btn-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(invoice);
                            }}
                          >
                            Atuar <ArrowRight size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* IA Insights Section */}
      <div className="incoming-dashboard-insights">
        <div className="incoming-dashboard-insights-content">
          <div className="incoming-dashboard-insights-left">
            <div className="incoming-dashboard-insights-icon">
              <Sparkles size={32} className="incoming-dashboard-insights-sparkle" />
            </div>
            <div>
              <h3 className="incoming-dashboard-insights-title">
                Otimização de Armazenagem Inteligente
              </h3>
              <p className="incoming-dashboard-insights-text">
                O Gemini IA analisou as cargas recebidas hoje. Sugerimos priorizar a conferência das notas fiscais pendentes para garantir o fluxo de estoque.
              </p>
            </div>
          </div>
          <button className="incoming-dashboard-insights-btn">
            Priorizar Conferência IA
          </button>
        </div>
      </div>

      {/* XML Import Modal */}
      {isImportModalOpen && (
        <div className="incoming-dashboard-modal-overlay" onClick={() => !isUploading && setIsImportModalOpen(false)}>
          <div className="incoming-dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-dashboard-modal-content">
              <div className="incoming-dashboard-modal-header">
                <div className="incoming-dashboard-modal-header-left">
                  <div className="incoming-dashboard-modal-icon">
                    <FileUp size={24} />
                  </div>
                  <div>
                    <h2 className="incoming-dashboard-modal-title">Importar XML NFe</h2>
                    <p className="incoming-dashboard-modal-subtitle">Padrão XML SEFAZ / xmlns</p>
                  </div>
                </div>
                {!isUploading && (
                  <button
                    onClick={() => setIsImportModalOpen(false)}
                    className="incoming-dashboard-modal-close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="incoming-dashboard-modal-body">
                <div className={`incoming-dashboard-upload-area ${isUploading ? 'uploading' : ''}`}>
                  {isUploading ? (
                    <div className="incoming-dashboard-upload-loading">
                      <Loader2 size={40} className="incoming-dashboard-spinner" />
                      <p className="incoming-dashboard-upload-text">Processando NFe...</p>
                      <p className="incoming-dashboard-upload-subtext">Validando Assinatura Digital e Itens</p>
                    </div>
                  ) : (
                    <div className="incoming-dashboard-upload-content">
                      <div className="incoming-dashboard-upload-icon">
                        <UploadCloud size={32} />
                      </div>
                      <p className="incoming-dashboard-upload-text">Arraste o arquivo XML aqui</p>
                      <p className="incoming-dashboard-upload-subtext">ou clique para selecionar do seu computador</p>
                      <input
                        type="file"
                        accept=".xml,application/xml,text/xml"
                        onChange={handleFileSelect}
                        className="incoming-dashboard-file-input"
                        id="xml-file-input"
                      />
                      <label htmlFor="xml-file-input" className="incoming-dashboard-file-label">
                        Selecionar Arquivo
                      </label>
                      {selectedFile && (
                        <div className="incoming-dashboard-file-selected">
                          <FileText size={16} />
                          <span>{selectedFile.name}</span>
                          <button
                            onClick={() => setSelectedFile(null)}
                            className="incoming-dashboard-file-remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                      {selectedFile && (
                        <button
                          onClick={handleFileUpload}
                          className="incoming-dashboard-upload-btn"
                          disabled={isUploading}
                        >
                          {isUploading ? 'Enviando...' : 'Enviar XML'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="incoming-dashboard-modal-info">
                  <Info size={18} className="incoming-dashboard-info-icon" />
                  <p className="incoming-dashboard-info-text">
                    Ao importar o XML, o sistema Medik realizará o "De-Para" automático de SKUs e converterá unidades de medida para o padrão de estoque. Os itens serão validados contra a requisição de compra.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Entry from XML Modal (without requisition) */}
      {isCreateModalOpen && (
        <div className="incoming-dashboard-modal-overlay" onClick={() => !isCreating && setIsCreateModalOpen(false)}>
          <div className="incoming-dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-dashboard-modal-content">
              <div className="incoming-dashboard-modal-header">
                <div className="incoming-dashboard-modal-header-left">
                  <div className="incoming-dashboard-modal-icon">
                    <FileUp size={24} />
                  </div>
                  <div>
                    <h2 className="incoming-dashboard-modal-title">Importar XML NFe (Sem Requisição)</h2>
                    <p className="incoming-dashboard-modal-subtitle">Criar nota fiscal diretamente do XML - Excepcionalidade</p>
                  </div>
                </div>
                {!isCreating && (
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="incoming-dashboard-modal-close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="incoming-dashboard-modal-body">
                <div className={`incoming-dashboard-upload-area ${isCreating ? 'uploading' : ''}`}>
                  {isCreating ? (
                    <div className="incoming-dashboard-upload-loading">
                      <Loader2 size={40} className="incoming-dashboard-spinner" />
                      <p className="incoming-dashboard-upload-text">Criando Nota Fiscal...</p>
                      <p className="incoming-dashboard-upload-subtext">Processando XML e criando entrada de mercadoria</p>
                    </div>
                  ) : (
                    <div className="incoming-dashboard-upload-content">
                      <div className="incoming-dashboard-upload-icon">
                        <UploadCloud size={32} />
                      </div>
                      <p className="incoming-dashboard-upload-text">Arraste o arquivo XML aqui</p>
                      <p className="incoming-dashboard-upload-subtext">ou clique para selecionar do seu computador</p>
                      <input
                        type="file"
                        accept=".xml,application/xml,text/xml"
                        onChange={handleFileSelectForCreate}
                        className="incoming-dashboard-file-input"
                        id="xml-file-input-create"
                      />
                      <label htmlFor="xml-file-input-create" className="incoming-dashboard-file-label">
                        Selecionar Arquivo
                      </label>
                      {selectedFileForCreate && (
                        <div className="incoming-dashboard-file-selected">
                          <FileText size={16} />
                          <span>{selectedFileForCreate.name}</span>
                          <button
                            onClick={() => setSelectedFileForCreate(null)}
                            className="incoming-dashboard-file-remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                      {selectedFileForCreate && (
                        <button
                          onClick={handleCreateFromXml}
                          className="incoming-dashboard-upload-btn"
                          disabled={isCreating}
                        >
                          {isCreating ? 'Criando...' : 'Criar Nota Fiscal do XML'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="incoming-dashboard-modal-info">
                  <Info size={18} className="incoming-dashboard-info-icon" />
                  <p className="incoming-dashboard-info-text">
                    <strong>Atenção:</strong> Esta funcionalidade permite criar uma nota fiscal diretamente do XML sem uma requisição de compra associada. 
                    O sistema criará automaticamente o fornecedor (se não existir) e processará os itens do XML. 
                    Use apenas em casos excepcionais quando não houver requisição de compra correspondente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Component */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default IncomingDashboard;
